import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, Info } from "lucide-react";

export function SweepReconciliation({ nonce = 0 }) {
  const { currency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sweeps/reconciliation?currency=${currency}`);
      setData(data);
      const d = {};
      (data.accounts || []).forEach((a) => {
        d[a.bank_account_id] = a.actual_closing ?? "";
      });
      setDrafts(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load sweep reconciliation");
    } finally {
      setLoading(false);
    }
  }, [currency]);

  useEffect(() => {
    load();
  }, [load, nonce]);

  const save = async (accId) => {
    setSavingId(accId);
    try {
      await api.post("/sweeps/actual-balance", {
        bank_account_id: accId,
        actual_closing: drafts[accId] === "" ? null : Number(drafts[accId]),
      });
      toast.success("Bank sweep balance recorded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSavingId("");
    }
  };

  if (loading) {
    return (
      <Card className="p-10 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
      </Card>
    );
  }
  if (!data) return null;

  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="sweep-recon">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Opening", t.opening, "sweep-kpi-opening"],
          ["Ins (into sweep)", t.ins, "sweep-kpi-ins"],
          ["Outs (back to bank)", t.outs, "sweep-kpi-outs"],
          ["Interest credited", t.interest, "sweep-kpi-interest"],
          ["Computed closing", t.computed_closing, "sweep-kpi-closing"],
        ].map(([label, value, tid]) => (
          <Card key={tid} className="p-4" data-testid={tid}>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
            <p className="font-heading text-lg mt-1.5 font-mono">
              {formatMoney(value, currency, { compact: true })}
            </p>
          </Card>
        ))}
      </div>

      <Card className="p-4 flex items-start gap-2.5" data-testid="sweep-method">
        <Info className="h-4 w-4 text-brand mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">{data.method}</p>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sweep-recon-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
              <th className="text-left px-4 py-3">Account</th>
              <th className="text-right px-4 py-3">Opening</th>
              <th className="text-right px-4 py-3">Ins</th>
              <th className="text-right px-4 py-3">Outs</th>
              <th className="text-right px-4 py-3">Interest</th>
              <th className="text-right px-4 py-3">Computed closing</th>
              <th className="text-right px-4 py-3">As per bank</th>
              <th className="text-right px-4 py-3">Difference</th>
            </tr>
          </thead>
          <tbody>
            {data.accounts.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No sweep movements booked yet.
                </td>
              </tr>
            )}
            {data.accounts.map((a) => (
              <tr
                key={a.bank_account_id || a.account_name}
                className="border-b border-white/[0.03]"
                data-testid={`sweep-row-${a.bank_account_id || "unassigned"}`}
              >
                <td className="px-4 py-2.5 text-xs">
                  {a.account_name}
                  <span className="text-muted-foreground"> · {a.bank}</span>
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">
                  {formatMoney(a.opening, currency, { compact: true })}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-300">
                  {formatMoney(a.ins, currency, { compact: true })}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-rose-300">
                  {formatMoney(a.outs, currency, { compact: true })}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">
                  {formatMoney(a.interest, currency, { compact: true })}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">
                  {formatMoney(a.computed_closing, currency, { compact: true })}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Input
                      value={drafts[a.bank_account_id] ?? ""}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [a.bank_account_id]: e.target.value }))
                      }
                      placeholder="enter"
                      className="h-8 w-[120px] text-right font-mono text-xs"
                      data-testid={`sweep-actual-input-${a.bank_account_id}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => save(a.bank_account_id)}
                      disabled={savingId === a.bank_account_id}
                      data-testid={`sweep-actual-save-${a.bank_account_id}`}
                    >
                      {savingId === a.bank_account_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </td>
                <td
                  className={`px-4 py-2.5 text-right font-mono text-xs ${
                    a.difference === null
                      ? "text-muted-foreground"
                      : a.tallied
                      ? "text-emerald-300"
                      : "text-rose-300"
                  }`}
                >
                  {a.difference === null
                    ? "—"
                    : a.tallied
                    ? "Tallied"
                    : formatMoney(a.difference, currency, { compact: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="overflow-x-auto">
        <div className="px-4 pt-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Fixed Deposits · accrued interest
          </p>
          <p className="text-xs text-muted-foreground font-mono">
            Principal {formatMoney(t.fd_principal, currency, { compact: true })} · Accrued{" "}
            {formatMoney(t.fd_accrued, currency, { compact: true })}
          </p>
        </div>
        <table className="w-full text-sm mt-3" data-testid="fd-accrual-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
              <th className="text-left px-4 py-3">FD No</th>
              <th className="text-left px-4 py-3">Bank</th>
              <th className="text-right px-4 py-3">Principal</th>
              <th className="text-right px-4 py-3">Rate</th>
              <th className="text-left px-4 py-3">Start</th>
              <th className="text-left px-4 py-3">Maturity</th>
              <th className="text-right px-4 py-3">Days</th>
              <th className="text-right px-4 py-3">Accrued</th>
              <th className="text-right px-4 py-3">Value to date</th>
            </tr>
          </thead>
          <tbody>
            {data.fds.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No fixed deposits on file.
                </td>
              </tr>
            )}
            {data.fds.map((f) => (
              <tr key={f.id} className="border-b border-white/[0.03]" data-testid={`fd-row-${f.id}`}>
                <td className="px-4 py-2.5 text-xs font-mono">{f.receipt_no}</td>
                <td className="px-4 py-2.5 text-xs">{f.bank}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">
                  {formatMoney(f.principal, currency, { compact: true })}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">
                  {f.rate ? `${f.rate}%` : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs font-mono">{f.start_date || "—"}</td>
                <td className="px-4 py-2.5 text-xs font-mono">{f.maturity_date || "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">{f.days_held}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-300">
                  {formatMoney(f.accrued_interest, currency, { compact: true })}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">
                  {formatMoney(f.value_to_date, currency, { compact: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
